import * as React from "react";
import { useDeviceStore } from "../lib/store/useDeviceStore";
import { useModalStore } from "../lib/store/useModalStore";
import type { NicePayRequestResponse } from "../@apis/dto/response/payment.response";

type NicePayParams = Record<string, string | number | null>;

/**
 * NICEPAY 결제 모듈 실행 훅.
 * 백엔드에서 받은 nicePayRequest 파라미터로 hidden form을 만들어 결제창을 띄운다.
 * PaymentButton과 동일한 메커니즘을 접수 플로우에서 재사용하기 위한 전용 훅.
 */
export function useNicePayLauncher() {
  const { deviceType } = useDeviceStore();
  const { openModal } = useModalStore();

  const [params, setParams] = React.useState<NicePayParams>();
  const formRef = React.useRef<HTMLFormElement>(null);

  React.useEffect(() => {
    window.__nicepaySubmitHandler = () => {
      openModal("ALERT_DIALOG", {
        title: "결제 완료",
        content: "결제가 완료되었습니다.",
        onConfirm: () => {
          if (formRef.current) {
            formRef.current.submit();
          }
        },
      });
    };

    window.__nicepayCloseHandler = () => {
      openModal("ALERT_DIALOG", {
        title: "결제 취소",
        content: "결제가 취소되었습니다.",
      });
    };

    return () => {
      window.__nicepaySubmitHandler = undefined;
      window.__nicepayCloseHandler = undefined;
    };
  }, []);

  React.useEffect(() => {
    if (params && formRef.current) {
      if (deviceType === "web") {
        window.goPay(formRef.current);
      } else {
        formRef.current.action = "https://web.nicepay.co.kr/v3/v3Payment.jsp";
        formRef.current.acceptCharset = "euc-kr";
        formRef.current.submit();
      }
    }
  }, [deviceType, params]);

  // 결제창 실행. ReqReserved 는 응답값을 그대로 사용하고,
  // ReturnURL/WapUrl/IspCancelUrl 만 클라이언트 환경에 맞춰 덮어쓴다.
  const launch = (nicePayRequest: NicePayRequestResponse) => {
    setParams({
      ...nicePayRequest,
      ReturnURL:
        import.meta.env.VITE_API_BASE_URL +
        "/v1/pay/nice-request/send-auth-response-and-pay-approve/by-form",
      WapUrl: "voicepark-pay://",
      IspCancelUrl: "voicepark-pay://",
    });
  };

  const formElement = params ? (
    <form
      name="payForm"
      ref={formRef}
      action={String(params.ReturnURL ?? "")}
      method="post"
      acceptCharset="EUC-KR"
    >
      {Object.entries(params).map(([key, value]) => (
        <input type="hidden" key={key} name={key} value={value ?? ""} />
      ))}
    </form>
  ) : null;

  return { launch, formElement };
}
